import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from 'sonner';
import api from '../../lib/api';
import { FileText, Calendar, ChevronLeft, ChevronRight, Search, Loader2, BarChart3, CheckCircle2, XCircle, UserX, Clock, PlusCircle } from 'lucide-react';

const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;

const AdminResumenDiario = () => {
  const today = new Date().toISOString().split('T')[0];
  const [fecha, setFecha] = useState(today);
  const [zona, setZona] = useState('');
  const [resumen, setResumen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('resumen');

  const fetchResumen = async (f = fecha, z = zona) => {
    setLoading(true);
    try {
      const params = { fecha: f };
      if (z) params.zona = z;
      const response = await api.get('/api/resumen-diario', { params });
      setResumen(response.data);
    } catch (error) {
      toast.error('Error al cargar el resumen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchResumen(); }, []);

  const cambiarDia = (offset) => {
    const d = new Date(fecha);
    d.setDate(d.getDate() + offset);
    const nuevaFecha = d.toISOString().split('T')[0];
    setFecha(nuevaFecha);
    fetchResumen(nuevaFecha, zona);
  };

  const irAHoy = () => {
    setFecha(today);
    fetchResumen(today, zona);
  };

  const exportPDF = () => {
    let url = `${API}/api/resumen-diario/export/pdf?fecha=${fecha}`;
    if (zona) url += `&zona=${zona}`;
    window.open(url, '_blank');
    toast.success(`Generando PDF para el ${formatDateDisplay(fecha)}...`);
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const getEstadoBadge = (estado) => {
    const styles = {
      'Realizada':  'bg-emerald-100 text-emerald-800 border-emerald-200',
      'Ausente':    'bg-slate-100 text-slate-700 border-slate-200',
      'Cancelada':  'bg-red-100 text-red-800 border-red-200',
      'Pendiente':  'bg-orange-100 text-orange-800 border-orange-200',
      'Notificada': 'bg-blue-100 text-blue-800 border-blue-200',
      'Asignada':   'bg-amber-100 text-amber-800 border-amber-200',
    };
    return styles[estado] || 'bg-slate-100 text-slate-600 border-slate-200';
  };

  return (
    <div className="space-y-5" data-testid="admin-resumen-diario">
      {/* Header with date nav + PDF button */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-0.5">Informe</p>
          <h1 className="text-2xl font-bold text-slate-900">Resumen Diario</h1>
          <p className="text-sm text-slate-500 mt-0.5 capitalize">{formatDateDisplay(fecha)}</p>
        </div>
        {/* Prominent PDF button */}
        <Button
          onClick={exportPDF}
          className="bg-red-600 hover:bg-red-700 text-white shadow-sm px-5 py-2.5 text-sm font-semibold"
          data-testid="btn-export-pdf"
        >
          <FileText className="w-4 h-4 mr-2" />
          Descargar PDF
        </Button>
      </div>

      {/* Date Navigator + Filters */}
      <Card className="border-slate-200 shadow-none">
        <CardContent className="py-4">
          {/* Date navigation row */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center bg-teal-50 border border-teal-200 rounded-xl px-3 py-2 gap-2 flex-1 max-w-lg">
              <Calendar className="w-4 h-4 text-teal-600" />
              <button onClick={() => cambiarDia(-1)} className="p-0.5 hover:bg-teal-100 rounded transition-colors" data-testid="btn-dia-prev">
                <ChevronLeft className="w-5 h-5 text-teal-700" />
              </button>
              <input
                type="date"
                value={fecha}
                onChange={(e) => { setFecha(e.target.value); fetchResumen(e.target.value, zona); }}
                className="border border-teal-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-teal-300"
                data-testid="input-fecha"
              />
              <button onClick={() => cambiarDia(1)} className="p-0.5 hover:bg-teal-100 rounded transition-colors" data-testid="btn-dia-next">
                <ChevronRight className="w-5 h-5 text-teal-700" />
              </button>
              {fecha !== today && (
                <button onClick={irAHoy} className="text-xs px-2 py-1 bg-teal-600 text-white rounded-md hover:bg-teal-700 ml-1" data-testid="btn-hoy">
                  Hoy
                </button>
              )}
            </div>

            <Select value={zona || 'todas'} onValueChange={(val) => { const z = val === 'todas' ? '' : val; setZona(z); fetchResumen(fecha, z); }}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Todas las zonas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas las zonas</SelectItem>
                <SelectItem value="Casco Antiguo">Casco Antiguo</SelectItem>
                <SelectItem value="Puerta Tierra">Puerta Tierra</SelectItem>
                <SelectItem value="Cádiz">Cádiz</SelectItem>
              </SelectContent>
            </Select>

            <Button onClick={() => fetchResumen(fecha, zona)} disabled={loading} variant="outline" data-testid="btn-buscar">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            </Button>
          </div>
        </CardContent>
      </Card>

      {resumen && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Programadas', value: resumen.resumen.total_programadas, color: 'bg-blue-50 border-blue-200 text-blue-700', icon: Calendar },
              { label: 'Realizadas',  value: resumen.resumen.realizadas,         color: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: CheckCircle2 },
              { label: 'Ausentes',    value: resumen.resumen.ausentes,            color: 'bg-slate-50 border-slate-200 text-slate-600', icon: UserX },
              { label: 'Canceladas',  value: resumen.resumen.canceladas,          color: 'bg-red-50 border-red-200 text-red-700', icon: XCircle },
              { label: 'Pendientes',  value: resumen.resumen.pendientes,          color: 'bg-orange-50 border-orange-200 text-orange-700', icon: Clock },
              { label: 'Creadas Hoy', value: resumen.resumen.tickets_creados_hoy, color: 'bg-teal-50 border-teal-200 text-teal-700', icon: PlusCircle },
            ].map(({ label, value, color, icon: Icon }) => (
              <div key={label} className={`${color} border rounded-xl p-4`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <Icon className="w-3.5 h-3.5" />
                  <p className="text-xs font-bold uppercase tracking-wide">{label}</p>
                </div>
                <p className="text-3xl font-bold">{value ?? 0}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-2 border-b border-slate-200 pb-2">
            <button
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'resumen' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:text-slate-900'}`}
              onClick={() => setActiveTab('resumen')}
              data-testid="tab-detalle"
            >
              Detalle completo
            </button>
            <button
              className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'comentarios' ? 'bg-teal-600 text-white' : 'text-slate-600 hover:text-slate-900'}`}
              onClick={() => setActiveTab('comentarios')}
              data-testid="tab-comentarios"
            >
              Comentarios ({resumen.comentarios.length})
            </button>
          </div>

          {activeTab === 'resumen' && (
            <div className="grid md:grid-cols-2 gap-5">
              {/* Realizadas */}
              <Card className="border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-emerald-700 flex items-center gap-2 text-base">
                    <CheckCircle2 className="w-4 h-4" /> Realizadas ({resumen.detalle.realizadas.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {resumen.detalle.realizadas.length === 0 ? (
                    <p className="text-slate-500 text-sm">Sin recogidas realizadas</p>
                  ) : (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {resumen.detalle.realizadas.map(ticket => (
                        <div key={ticket.ticket_id} className="p-2 bg-emerald-50 rounded-lg text-sm">
                          <span className="font-semibold text-emerald-800">{ticket.ticket_id}</span>
                          <span className="text-slate-600 ml-2">{ticket.direccion}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Ausentes */}
              <Card className="border-slate-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-slate-600 flex items-center gap-2 text-base">
                    <UserX className="w-4 h-4" /> Ausentes ({resumen.detalle.ausentes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {resumen.detalle.ausentes.length === 0 ? (
                    <p className="text-slate-500 text-sm">Sin ausentes</p>
                  ) : (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {resumen.detalle.ausentes.map(ticket => (
                        <div key={ticket.ticket_id} className="p-2 bg-slate-50 rounded-lg text-sm">
                          <span className="font-semibold text-slate-700">{ticket.ticket_id}</span>
                          <span className="text-slate-500 ml-2">{ticket.direccion}</span>
                          {ticket.hora_visita && (
                            <span className="text-slate-500 ml-2 text-xs">· {ticket.hora_visita}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Canceladas */}
              <Card className="border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-red-700 flex items-center gap-2 text-base">
                    <XCircle className="w-4 h-4" /> Canceladas ({resumen.detalle.canceladas.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {resumen.detalle.canceladas.length === 0 ? (
                    <p className="text-slate-500 text-sm">Sin cancelaciones</p>
                  ) : (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {resumen.detalle.canceladas.map(ticket => (
                        <div key={ticket.ticket_id} className="p-2 bg-red-50 rounded-lg text-sm">
                          <span className="font-semibold text-red-800">{ticket.ticket_id}</span>
                          <span className="text-slate-600 ml-2">{ticket.direccion}</span>
                          {ticket.motivo_cancelacion && (
                            <Badge variant="destructive" className="ml-2 text-xs">{ticket.motivo_cancelacion}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pendientes */}
              <Card className="border-orange-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-orange-700 flex items-center gap-2 text-base">
                    <Clock className="w-4 h-4" /> Pendientes ({resumen.detalle.pendientes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {resumen.detalle.pendientes.length === 0 ? (
                    <p className="text-slate-500 text-sm">Sin pendientes</p>
                  ) : (
                    <div className="space-y-1.5 max-h-60 overflow-y-auto">
                      {resumen.detalle.pendientes.map(ticket => (
                        <div key={ticket.ticket_id} className="p-2 bg-orange-50 rounded-lg text-sm">
                          <span className="font-semibold text-orange-800">{ticket.ticket_id}</span>
                          <span className="text-gray-600 ml-2">{ticket.direccion}</span>
                          <Badge className={`ml-2 text-xs ${getEstadoBadge(ticket.estado)}`}>
                            {ticket.estado}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'comentarios' && (
            <Card>
              <CardHeader>
                <CardTitle>💬 Comentarios del Día</CardTitle>
              </CardHeader>
              <CardContent>
                {resumen.comentarios.length === 0 ? (
                  <p className="text-gray-500">Sin comentarios registrados</p>
                ) : (
                  <div className="space-y-3">
                    {resumen.comentarios.map((com, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-blue-700">{com.ticket_id}</span>
                          <Badge className={getEstadoBadge(com.estado)}>{com.estado}</Badge>
                          {com.hora && (
                            <span className="text-gray-500 text-sm">
                              {new Date(com.hora).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                        <p className="text-gray-700">{com.comentario}</p>
                        {com.usuario && (
                          <p className="text-xs text-gray-500 mt-1">Por: {com.usuario}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default AdminResumenDiario;
